import { PlayArrow, Settings } from '@mui/icons-material';
import { Button, CardActions, CardContent, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { RootState } from '../../../../../Redux/ReduxStore';
import { UserAccessState } from '../../../../../Redux/Slices/UserAccessSlice';
import { CommodityType, CalculateMatrixResponse, MatrixCalculationType, MatrixContractSettlement, MatrixStripType, MatrixTerm, MarketViewProductModel } from '../../../../../Generated/Mark-It-View-Api';
import { EnumHelper } from '../../../../../Helpers';
import { useLoadingState, useMtmDate } from '../../../../../Hooks';
import { StoneXRow } from '../../../../StoneX';
import MarkItViewProductSelector from '../../../../StoneX/MarkItViewProductSelector.tsx/MarkItViewProductSelector';
import { StoneXButton, StoneXDatePicker, StoneXSelect, StoneXTextField, StoneXToggleButton } from '../../../../StoneXMui';
import { MatrixSavedParameters } from '../../../../../Types/MatrixSavedParameters';
import { MarkItViewApi } from '../../../../../Apis/Apis';
import {
  MatrixContractSettlementLabels,
  MatrixStripTypeLabels,
  MatrixTermLabels,
} from '../../../Tools/Matrix/MatrixLabels';

interface MatrixFormProps {
  savedParams?: MatrixSavedParameters | null;
  onMatrixResult: (result: CalculateMatrixResponse | null) => void;
  onParamsUpdate: (params: MatrixSavedParameters | null) => void;
  onLoadingChange: (loading: boolean) => void;
}

const allTerms = Object.values(MatrixTerm);
const allStripTypes = Object.values(MatrixStripType);
const allSettlements = Object.values(MatrixContractSettlement);
const allMonths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const monthLabels: Record<number, string> = { 1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun', 7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec' };

export default function MatrixForm({ savedParams, onMatrixResult, onParamsUpdate, onLoadingChange }: MatrixFormProps) {
  const userAccess = useApplicationSelector((state: RootState): UserAccessState => state.userAccess);
  const isEmployee = userAccess.isEmployee;
  const quoteDate = useMtmDate();

  // Basic options
  const [selectedCommodityType, setSelectedCommodityType] = useState<CommodityType>(CommodityType.Favorites);
  const [selectedProduct, setSelectedProduct] = useState<MarketViewProductModel | null>(null);
  const [startQuoteDate, setStartQuoteDate] = useState<Date | null>(dayjs(quoteDate).subtract(1, 'year').toDate());
  const [endQuoteDate, setEndQuoteDate] = useState<Date | null>(quoteDate);
  const [stripType, setStripType] = useState<MatrixStripType>(MatrixStripType.RollingMonth);
  const [contractSettlement, setContractSettlement] = useState<MatrixContractSettlement>(MatrixContractSettlement.ExpirationDate);
  const [selectedTerm, setSelectedTerm] = useState<MatrixTerm>(MatrixTerm.Annual);
  const [customMonths, setCustomMonths] = useState<number[]>([]);
  const [startMonth, setStartMonth] = useState<number>(0);
  const [selectedPercentiles, setSelectedPercentiles] = useState<number[]>([0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]);

  // Advanced options (employee only)
  const [showAdvancedOptions, setShowAdvancedOptions] = useState<boolean>(false);
  const [weighted, setWeighted] = useState<boolean>(true);
  const [innerRange, setInnerRange] = useState<number>(5);
  const [outerRange, setOuterRange] = useState<number>(20);
  const [innerWeight, setInnerWeight] = useState<number>(75);
  const [outerWeight, setOuterWeight] = useState<number>(25);
  const [yearsBack, setYearsBack] = useState<number>(5);
  const [applyPpi, setApplyPpi] = useState<boolean>(false);
  const [aboveMedian, setAboveMedian] = useState<boolean>(false);

  // Track auto-run for saved-query loads
  const [autoRunPending, setAutoRunPending] = useState<boolean>(!!savedParams?.productId);

  const reportLoadingState = useLoadingState();

  // Load saved params on mount
  useEffect(() => {
    if (savedParams) {
      if (savedParams.startDate) {
        setStartQuoteDate(new Date(savedParams.startDate));
      }
      if (savedParams.endDate) {
        setEndQuoteDate(new Date(savedParams.endDate));
      }
      if (savedParams.matrixStripType) {
        setStripType(savedParams.matrixStripType as MatrixStripType);
      }
      if (savedParams.contractSettlement) {
        setContractSettlement(savedParams.contractSettlement as MatrixContractSettlement);
      }
      if (savedParams.terms && savedParams.terms.length > 0) {
        setSelectedTerm(savedParams.terms[0] as MatrixTerm);
      }
      if (savedParams.months) {
        setCustomMonths(savedParams.months);
      }
      if (savedParams.startMonth != null) {
        setStartMonth(savedParams.startMonth);
      }
      if (savedParams.selectedPercentiles) {
        setSelectedPercentiles(savedParams.selectedPercentiles);
      }
      // Advanced
      if (savedParams.weighted != null) {
        setWeighted(savedParams.weighted);
      }
      if (savedParams.innerRange != null) {
        setInnerRange(savedParams.innerRange);
      }
      if (savedParams.outerRange != null) {
        setOuterRange(savedParams.outerRange);
      }
      if (savedParams.innerWeight != null) {
        setInnerWeight(savedParams.innerWeight * 100);
      }
      if (savedParams.outerWeight != null) {
        setOuterWeight(savedParams.outerWeight * 100);
      }
      if (savedParams.yearsBack != null) {
        setYearsBack(savedParams.yearsBack);
      }
      if (savedParams.applyPpi != null) {
        setApplyPpi(savedParams.applyPpi);
      }
      if (savedParams.aboveMedian != null) {
        setAboveMedian(savedParams.aboveMedian);
      }
    }
  }, []);

  // Notify parent of params changes
  useEffect(() => {
    if (!selectedProduct) {
      onParamsUpdate(null);
      return;
    }

    const params: MatrixSavedParameters = {
      productId: selectedProduct.productId!,
      startDate: startQuoteDate ? dayjs(startQuoteDate).format('YYYY-MM-DD') : null,
      endDate: endQuoteDate ? dayjs(endQuoteDate).format('YYYY-MM-DD') : null,
      matrixStripType: stripType,
      contractSettlement: contractSettlement,
      terms: [selectedTerm],
      months: selectedTerm === MatrixTerm.Custom ? customMonths : null,
      startMonth: startMonth > 0 ? startMonth : null,
      selectedPercentiles: selectedPercentiles,
    };

    // Only include advanced params if employee
    if (isEmployee) {
      params.weighted = weighted;
      params.innerRange = innerRange;
      params.outerRange = outerRange;
      params.innerWeight = innerWeight / 100;
      params.outerWeight = outerWeight / 100;
      params.yearsBack = weighted ? undefined : yearsBack;
      params.applyPpi = applyPpi;
      params.aboveMedian = aboveMedian;
    }

    onParamsUpdate(params);
  }, [selectedProduct, startQuoteDate, endQuoteDate, stripType, contractSettlement, selectedTerm, customMonths, startMonth, selectedPercentiles, weighted, innerRange, outerRange, innerWeight, outerWeight, yearsBack, applyPpi, aboveMedian]);

  function runDeciles() {
    if (!selectedProduct) { return; }

    const request = {
      productId: selectedProduct.productId!,
      startDate: dayjs(startQuoteDate).format('YYYY-MM-DD'),
      matrixStripType: stripType,
      contractSettlement: contractSettlement,
      weighted: isEmployee ? weighted : true,
      innerRange: isEmployee ? innerRange : 5,
      outerRange: isEmployee ? outerRange : 20,
      innerWeight: isEmployee ? innerWeight / 100 : 0.8,
      outerWeight: isEmployee ? outerWeight / 100 : 0.2,
      applyPpi: isEmployee ? applyPpi : false,
      aboveMedian: isEmployee ? aboveMedian : false,
      terms: [selectedTerm],
      months: selectedTerm === MatrixTerm.Custom ? customMonths : undefined,
      startMonth: startMonth > 0 ? startMonth : undefined,
      calculationDate: dayjs(endQuoteDate).format('YYYY-MM-DD'),
      calculationType: MatrixCalculationType.Monthly,
    };

    onLoadingChange(true);
    MarkItViewApi.calculateMatrix(reportLoadingState.setLoadingState, request)
      .then((res) => onMatrixResult(res.data))
      .catch(() => onMatrixResult(null))
      .finally(() => onLoadingChange(false));
  }

  // Auto-run once when saved params are loaded and the product has been resolved.
  useEffect(() => {
    if (autoRunPending && selectedProduct) {
      setAutoRunPending(false);
      runDeciles();
    }
  }, [autoRunPending, selectedProduct]);

  return (
    <>
      <CardContent sx={{ flexGrow: 1, padding: '40px' }}>
        <Grid container spacing={2}>
          {/* Dates */}
          <Grid container direction="column" xs="auto" item spacing={2}>
            <Grid item>
              <StoneXDatePicker label="Start Quote Date" value={startQuoteDate} onChange={setStartQuoteDate} />
            </Grid>
            <Grid item>
              <StoneXDatePicker label="End Quote Date" value={endQuoteDate} onChange={setEndQuoteDate} />
            </Grid>
          </Grid>

          {/* Commodity & Product */}
          <Grid container direction="column" xs="auto" item spacing={2}>
            <Grid item>
              <StoneXSelect
                options={Object.values(CommodityType).sort((a, b) => {
                  if (a === CommodityType.Favorites) { return -1; }
                  if (b === CommodityType.Favorites) { return -1; }
                  return a < b ? -1 : a > b ? 1 : 0;
                })}
                getId={(x) => x}
                value={selectedCommodityType}
                label="Commodity Type"
                onChange={(x) => setSelectedCommodityType(x!)}
                getOptionLabel={(x) => EnumHelper.enumToString(x)}
              />
            </Grid>
            <Grid item>
              <MarkItViewProductSelector
                commodityType={selectedCommodityType}
                initialProductId={savedParams?.productId}
                onProductChange={setSelectedProduct}
              />
            </Grid>
          </Grid>

          {/* Strip & Settlement */}
          <Grid container direction="column" xs="auto" item spacing={2}>
            <Grid item>
              <StoneXSelect
                value={stripType}
                onChange={(value) => setStripType(value ?? MatrixStripType.RollingMonth)}
                options={allStripTypes}
                getOptionLabel={(x) => MatrixStripTypeLabels[x]}
                getId={(x) => x}
                label="Strip Type"
              />
            </Grid>
            <Grid item>
              <StoneXSelect
                value={contractSettlement}
                onChange={(value) => setContractSettlement(value ?? MatrixContractSettlement.ExpirationDate)}
                options={allSettlements}
                getOptionLabel={(x) => MatrixContractSettlementLabels[x]}
                getId={(x) => x}
                label="Contract Settlement"
              />
            </Grid>
          </Grid>

          {/* Term */}
          <Grid container direction="column" xs="auto" item spacing={2}>
            <Grid item>
              <StoneXSelect
                value={selectedTerm}
                onChange={(value) => setSelectedTerm(value ?? MatrixTerm.Annual)}
                options={allTerms}
                getOptionLabel={(x: MatrixTerm) => MatrixTermLabels[x]}
                getId={(x: MatrixTerm) => x}
                label="Term"
              />
            </Grid>
            {selectedTerm === MatrixTerm.Custom && (
              <Grid item>
                <StoneXSelect
                  value={customMonths[0] ?? null}
                  onChange={(value) => setCustomMonths(value ? [value] : [])}
                  options={allMonths}
                  getOptionLabel={(x: number) => monthLabels[x]}
                  getId={(x: number) => x}
                  label="Custom Month"
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Advanced Options (employee only) */}
        {isEmployee && (
          <>
            <StoneXRow>
              <StoneXButton onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} variant="text" startIcon={<Settings />}>
                {showAdvancedOptions ? 'Hide Advanced Options' : 'Show Advanced Options'}
              </StoneXButton>
            </StoneXRow>

            {showAdvancedOptions && (
              <>
                <StoneXRow>
                  <StoneXToggleButton label="Weighted" value={weighted} onChange={setWeighted} />
                  <StoneXToggleButton label="Apply PPI" value={applyPpi} onChange={setApplyPpi} />
                  <StoneXToggleButton label="Above Median" value={aboveMedian} onChange={setAboveMedian} />
                </StoneXRow>
                {weighted ? (
                  <StoneXRow>
                    <StoneXTextField label="Inner Range (years)" type="number" value={innerRange} onChange={(e) => setInnerRange(Number(e.target.value))} />
                    <StoneXTextField label="Outer Range (years)" type="number" value={outerRange} onChange={(e) => setOuterRange(Number(e.target.value))} />
                    <StoneXTextField label="Inner Weight (%)" type="number" value={innerWeight} onChange={(e) => setInnerWeight(Number(e.target.value))} />
                    <StoneXTextField label="Outer Weight (%)" type="number" value={outerWeight} onChange={(e) => setOuterWeight(Number(e.target.value))} />
                  </StoneXRow>
                ) : (
                  <StoneXRow>
                    <StoneXTextField label="Years Back" type="number" value={yearsBack} onChange={(e) => setYearsBack(Number(e.target.value))} />
                  </StoneXRow>
                )}
              </>
            )}
          </>
        )}
      </CardContent>

      <CardActions sx={{ paddingLeft: '40px' }}>
        <Button onClick={runDeciles} disabled={!selectedProduct || reportLoadingState.isLoading()} startIcon={<PlayArrow />}>
          Run Matrix
        </Button>
      </CardActions>
    </>
  );
}
